import React, { ReactNode, useEffect, useState } from "react";
import { SearchActivity } from "../../components/GenericTable/SubComponents/TableHeader/TableSearchBar";
import { getListSearchThrottle } from "../../components/GenericTable/SubComponents/TableHeader/searchThrottle";
import { isTooShortToSearch } from "../../../utils/functions/listSearchThrottle";
import {
  ListSearchStatus,
  ListSearchStatusTexts,
} from "../components/list-search-status";

// Search feedback for a FunctionalList (design list, flag REACT_APP_DESIGN_
// SEARCH_FEEDBACK_ENABLED). Generic twin of the logo list's
// useLogoSearchFeedback (#806), which is left untouched: the list passes its
// own seven translation keys (`texts`) and its own status test id. After
// typing, a list with keepPreviousData only dims the previous rows and never
// shows a hit count; this turns the react-query state the list ALREADY has
// (isFetching / isPreviousData / totalCount) plus the search field's
// debounce window into three FunctionalList props: the spinner inside the
// field, the status line and the progress bar. 0 extra calls; the debounce
// and the query key are untouched here. With the list-search throttle on
// (flag listSearchThrottleEnabled) a draft under the minimum length is not
// "searching" - the field shows its hint instead of the spinner and the
// status line says the same (as the logo list's hook).
export interface ListSearchFeedbackInput {
  enabled: boolean;
  searchTerm: string | undefined;
  isFetching: boolean;
  isPreviousData: boolean;
  totalCount?: number;
  pageResultCount: number;
  onClear: () => void;
  texts: ListSearchStatusTexts;
  statusTestId?: string;
}

export interface ListSearchFeedbackProps {
  searchActivity?: SearchActivity;
  searchStatus?: ReactNode;
  showFetchProgress?: boolean;
}

// Flag off: one frozen empty object, so `{...props}` adds nothing at all.
const NONE: ListSearchFeedbackProps = Object.freeze({});

export function useListSearchFeedback({
  enabled,
  searchTerm,
  isFetching,
  isPreviousData,
  totalCount,
  pageResultCount,
  onClear,
  texts,
  statusTestId,
}: ListSearchFeedbackInput): ListSearchFeedbackProps {
  const term = searchTerm ?? "";
  // Debounce window of the search field (typed, not yet emitted).
  const [draft, setDraft] = useState<string | null>(null);
  // Term whose rows are on screen. With keepPreviousData a fetch for a NEW
  // key shows the old rows: a differing term = search-driven (field spinner
  // + "Searching…"), an equal term = paging/filter within the same search
  // (only the dim + progress bar, and the hit count stays valid).
  const [shownTerm, setShownTerm] = useState(term);
  const loadingNewKey = isFetching && isPreviousData;
  useEffect(() => {
    if (!loadingNewKey) setShownTerm(term);
  }, [loadingNewKey, term]);

  if (!enabled) return NONE;

  // Throttle off: minChars = 0, so nothing is ever too short (= #809 as is).
  const { minChars } = getListSearchThrottle();
  const tooShort = draft !== null && isTooShortToSearch(draft, minChars);
  const searchInFlight = loadingNewKey && term !== shownTerm;
  const isSearching = (draft !== null && !tooShort) || searchInFlight;
  return {
    searchActivity: { isSearching, onDraftChange: setDraft },
    searchStatus: (
      <ListSearchStatus
        term={draft ?? term}
        isSearching={isSearching}
        minCharsHint={tooShort ? minChars : undefined}
        totalCount={totalCount}
        pageResultCount={pageResultCount}
        onClear={onClear}
        texts={texts}
        testId={statusTestId}
      />
    ),
    showFetchProgress: true,
  };
}
